import { useForm } from "react-hook-form";
import { FiArrowLeft, FiEye } from "react-icons/fi";

import { type RelatedGroup } from "@/components/shared/BlockedDeleteDialog";
import { type CHLD } from "@/models/chld";
import { type FAMI } from "@/models/fami";
import { type LANG, type SLNG, type ENRL, type CLEN } from "@/models";
import { formatDateOnly } from "@/utils/dates";

import { useLeaData } from "@/queries.ts";
import {
  makeChldId,
  makeClenId,
  makeEnrlId,
  makeFamiId,
  makeLangId,
  makeSlngId,
} from "../hooks/useBlockedChildDeletion";
import Popup from "@/components/shared/Popup";
import { CHLDAddEditForm } from "@/components/records/child/CHLDAddEditForm";
import FAMIEditDialog from "../dialogs/FAMIEditDialog";
import LANGEditDialog from "../dialogs/LANGEditDialog";
import SLNGEditDialog from "../dialogs/SLNGEditDialog";
import ENRLEditDialog from "../dialogs/ENRLEditDialog";
import { getClassroomName } from "@/utils/classroom-lookup";
import CLENEditDialog from "../dialogs/CLENEditDialog";

type LeaContextLike = {
  currentOrgPreschools?: Array<{
    PreschoolCDSCode?: string;
    PreschoolName?: string;
  }>;
};

type LeaData = ReturnType<typeof useLeaData>["data"];

function CHLDViewPopup({
  child,
  onClose,
}: {
  child: CHLD;
  onClose: () => void;
}) {
  const form = useForm<CHLD>({ defaultValues: child });
  return (
    <Popup
      show={true}
      header={
        <div
          className="d-flex align-items-center gap-2"
          style={{ fontSize: "1.25rem" }}
        >
          <FiEye size={20} />
          <span>View Child Record</span>
        </div>
      }
      hasButton={false}
      backButtonText={
        <div className="d-flex align-items-center gap-2">
          <FiArrowLeft size={16} />
          <span>Back</span>
        </div>
      }
      size="lg"
      onClose={onClose}
      onBackButtonClick={onClose}
      canEdit={false}
    >
      <CHLDAddEditForm
        selectedChild={child}
        form={form}
        childDefaults={undefined}
      />
    </Popup>
  );
}

export function getChildBlockedGroups({
  // When provided, additional CHLD rows render as their own group (MID
  // resolve flow). Default empty array keeps the standard delete flow
  // unchanged.
  extraChldRecords = [],
  pendingChldDeletes = new Set<string>(),
  // The "anchor" CHLD — the one designated to be deleted last (via the
  // parent-delete path), so its deletion can be wrapped in a mutation
  // result callback that fires before this component is unmounted. While
  // `lockChldAnchor` is true, this row is visible in the list but
  // unselectable.
  chldAnchorId,
  lockChldAnchor = false,
  famiRecords,
  langRecords,
  slngRecords,
  enrlRecords,
  clenRecords,
  pendingFamiDeletes,
  pendingLangDeletes,
  pendingSlngDeletes,
  pendingEnrlDeletes,
  pendingClenDeletes,
  addFamiDelete,
  addLangDelete,
  addSlngDelete,
  addEnrlDelete,
  addClenDelete,
  leaContext,
  leaData,
}: {
  extraChldRecords?: CHLD[];
  pendingChldDeletes?: Set<string>;
  chldAnchorId?: string;
  lockChldAnchor?: boolean;
  famiRecords: FAMI[];
  langRecords: LANG[];
  slngRecords: SLNG[];
  enrlRecords: ENRL[];
  clenRecords: CLEN[];
  pendingFamiDeletes: Set<string>;
  pendingLangDeletes: Set<string>;
  pendingSlngDeletes: Set<string>;
  pendingEnrlDeletes: Set<string>;
  pendingClenDeletes: Set<string>;
  addFamiDelete: (id: string) => void;
  addLangDelete: (id: string) => void;
  addSlngDelete: (id: string) => void;
  addEnrlDelete: (id: string) => void;
  addClenDelete: (id: string) => void;
  leaContext: LeaContextLike;
  leaData: LeaData;
}): RelatedGroup[] {
  const preschoolName = (code?: string) =>
    leaContext.currentOrgPreschools?.find((p) => p.PreschoolCDSCode === code)
      ?.PreschoolName ??
    code ??
    "";

  const groups: RelatedGroup[] = [];

  if (extraChldRecords.length > 0) {
    groups.push({
      key: "chld",
      label: "Child (CHLD)",
      records: extraChldRecords
        .filter((r) => !pendingChldDeletes.has(makeChldId(r)))
        .map((r) => {
          const id = makeChldId(r);
          const isAnchor = id === chldAnchorId;
          const disabled = isAnchor && lockChldAnchor;
          return {
            id,
            primary: `${preschoolName(r.PreschoolCode)} · CICN ${
              r.LocalChildId
            } · ${r.ProgramYear}`,
            secondary: r.EffectiveStartDate
              ? `Effective: ${formatDateOnly(r.EffectiveStartDate)}`
              : "",
            disabled,
            disabledReason: disabled ? "Will be deleted last" : undefined,
            data: r,
          };
        }),
      renderView: (record, onClose) => (
        <CHLDViewPopup child={record as CHLD} onClose={onClose} />
      ),
    });
  }

  groups.push(
    {
      key: "fami",
      label: "Family (FAMI)",
      records: famiRecords
        .filter((r) => !pendingFamiDeletes.has(makeFamiId(r)))
        .map((r) => ({
          id: makeFamiId(r),
          primary: `${preschoolName(r.PreschoolCode)} · FICN ${
            r.LocalFamilyId
          } · ${r.ProgramYear}`,
          secondary: [
            [r.HeadOfHousehold1FirstName, r.HeadOfHousehold1LastName]
              .filter(Boolean)
              .join(" "),
            r.EffectiveStartDate ? formatDateOnly(r.EffectiveStartDate) : "",
          ]
            .filter(Boolean)
            .join(" · "),
          data: r,
        })),
      renderView: (record, onClose) => (
        <FAMIEditDialog
          selectedFamily={record as FAMI}
          onClose={onClose}
          viewOnly
          footerMode="back"
          onDeleted={(deleted) => addFamiDelete(makeFamiId(deleted))}
        />
      ),
    },
    {
      key: "lang",
      label: "Language (LANG)",
      records: langRecords
        .filter((r) => !pendingLangDeletes.has(makeLangId(r)))
        .map((r) => ({
          id: makeLangId(r),
          primary: `${preschoolName(r.PreschoolCode)} · CICN ${
            r.LocalChildId
          } · ${r.ProgramYear}`,
          secondary: r.EffectiveStartDate
            ? formatDateOnly(r.EffectiveStartDate)
            : "",
          data: r,
        })),
      renderView: (record, onClose) => (
        <LANGEditDialog
          selectedLang={record as LANG}
          onClose={onClose}
          viewOnly
          footerMode="back"
          onDeleted={(deleted) => addLangDelete(makeLangId(deleted))}
        />
      ),
    },
    {
      key: "slng",
      label: "Supplemental Language (SLNG)",
      records: slngRecords
        .filter((r) => !pendingSlngDeletes.has(makeSlngId(r)))
        .map((r) => ({
          id: makeSlngId(r),
          primary: `${preschoolName(r.PreschoolCode)} · CICN ${
            r.LocalChildId
          } · ${r.ProgramYear}`,
          secondary: r.EffectiveStartDate
            ? formatDateOnly(r.EffectiveStartDate)
            : "",
          data: r,
        })),
      renderView: (record, onClose) => (
        <SLNGEditDialog
          selectedSLNG={record as SLNG}
          onClose={onClose}
          viewOnly
          footerMode="back"
          onDeleted={(deleted) => addSlngDelete(makeSlngId(deleted))}
        />
      ),
    },
    {
      key: "enrl",
      label: "Enrollment (ENRL)",
      records: enrlRecords
        .filter((r) => !pendingEnrlDeletes.has(makeEnrlId(r)))
        .map((r) => ({
          id: makeEnrlId(r),
          primary: `${preschoolName(r.PreschoolCode)} · CICN ${
            r.LocalChildId
          } · ${r.ProgramYear}`,
          secondary: [
            r.EnrollmentStartDate
              ? `Start: ${formatDateOnly(r.EnrollmentStartDate)}`
              : "",
            r.EnrollmentEndDate
              ? `End: ${formatDateOnly(r.EnrollmentEndDate)}`
              : "",
          ]
            .filter(Boolean)
            .join(" · "),
          data: r,
        })),
      renderView: (record, onClose) => (
        <ENRLEditDialog
          selectedEnrollment={record as ENRL}
          onClose={onClose}
          viewOnly
          footerMode="back"
          onDeleted={(deleted) => addEnrlDelete(makeEnrlId(deleted))}
        />
      ),
    },
    {
      key: "clen",
      label: "Classroom Enrollment (CLEN)",
      records: clenRecords
        .filter((r) => !pendingClenDeletes.has(makeClenId(r)))
        .map((r) => ({
          id: makeClenId(r),
          primary: `${preschoolName(r.PreschoolCode)} · CICN ${
            r.LocalChildId
          } · ${r.ProgramYear}`,
          secondary: getClassroomName(
            leaData,
            r.PreschoolCode,
            r.ProgramYear,
            r.LocalClassroomId
          ),
          data: r,
        })),
      renderView: (record, onClose) => (
        <CLENEditDialog
          selectedCLEN={record as CLEN}
          onClose={onClose}
          viewOnly
          footerMode="back"
          onDeleted={(deleted) => addClenDelete(makeClenId(deleted))}
        />
      ),
    }
  );

  return groups;
}
